/**
 * This backend signal manages the reference areas used in the application.
 * 
 */

import { overpassJson } from "overpass-ts"
import osmtogeojson from "osmtogeojson"
import { computed, effect, signal } from "@preact/signals-react"
import cloneDeep from "lodash.clonedeep"
import area from "@turf/area"
import center from "@turf/center"
import bbox from "@turf/bbox"
import intersect from "@turf/intersect"
import bboxPolygon from "@turf/bbox-polygon"

import { fitBounds, flyTo } from "../components/MainMap/MapObservableStore"
import { layerVisibility, mapBounds, zoom } from "./mapSignals"
import { point, polygon } from "@turf/helpers"
import buffer from "@turf/buffer"

import { posthog } from "../tracking"
import { concave, distance } from "@turf/turf"


export type AreaSuggestions = GeoJSON.FeatureCollection<GeoJSON.Polygon>
export const areaSuggestions = signal<AreaSuggestions>({type: 'FeatureCollection', features: []})

interface ReferenceAreaProperties {
    id: string,
    origin: 'osm' | 'user',
    area: number,
    landuse?: string
}

// implement the actual reference area signal
export type ReferenceArea = GeoJSON.FeatureCollection<GeoJSON.Polygon, ReferenceAreaProperties>
export const referenceArea = signal<ReferenceArea>({type: 'FeatureCollection', features: []})

export const referenceAreaHectar = computed(() => {
    return area(referenceArea.value) / 10000
})

// add a effect to add the reference Area to the layer visibility
effect(() => {
    // check fot the reference area popping up
    if (referenceArea.value.features.length === 0) {
        // disable the reference area layer
        const { referenceArea, ...others } = layerVisibility.peek()
        layerVisibility.value = others
    }

    // otherwise if it did not exist before, add it
    else if (!Object.keys(layerVisibility.peek()).includes("referenceArea")) {
        layerVisibility.value = {
            ...layerVisibility.peek(),
            'referenceArea': "visible"
        }
    }
})

export const loadReferenceAreaSuggestions = (): Promise<void> => {
    const ll = mapBounds.peek()?.getSouthWest()
    const ur = mapBounds.peek()?.getNorthEast()
    const bbox = `${ll!.lat},${ll!.lng},${ur!.lat},${ur!.lng}`

    const query = `[out:json];
    (
        way[landuse=farmland](${bbox});
        way[landuse=meadow](${bbox});
        way[landuse=agricultural](${bbox});
    );
    (._;>;);
    out;`
    // console.log(query)
    
    return overpassJson(query).then((data)  => {
        // console.log(data)
        const parsedData = osmtogeojson(data)
        // console.log(parsedData)

        areaSuggestions.value = {
            type: 'FeatureCollection',
            features: [
                ...areaSuggestions.peek().features,
                ...parsedData.features as GeoJSON.Feature<GeoJSON.Polygon>[]
            ]
            .filter((f, i, self) => self.findIndex(s => s.id === f.id) === i)
        }
    })
}

// add a timeout for loading suggestions, as we only call the overpass api every second
const isRequestingSuggestions = signal(false)

// add an effect to load suggestions every time the map moves and there are no suggestions loaded for the area.
// This is only done, if the zoom level is close enough and there is not yet a reference area loaded.
effect(() => {
    // make sure that the trigger is on false
    // using value here, will trigger the effect right after a call has been made,
    // then it is checked after 1 second after each request if more requests are needed
    if (isRequestingSuggestions.value) return

    // first make sure that there is not yet a reference area
    if (referenceArea.value.features.length !== 0) return

    // then check that the zoom level is close enough
    if (zoom.value < 13) return
    
    // get the map bounds and check if they are within the bounding box of the suggestions
    const bounds = mapBounds.value
    if (!bounds) return

    console.log('Checking area suggestions coverage...')
    const mapBBox = polygon([[     
        [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
        [bounds.getSouthEast().lng, bounds.getSouthEast().lat],
        [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
        [bounds.getNorthWest().lng, bounds.getNorthWest().lat],
        [bounds.getSouthWest().lng, bounds.getSouthWest().lat]
    ]])

    // get the suggestions bounding box
    // if there are no features, the bbox will be [Infinity, Infinity, -Infinity, -Infinity]
    let intersectArea = 0
    if (areaSuggestions.value.features.length > 0) {
        const suggBbox =  bboxPolygon(bbox(areaSuggestions.value))

        // get the ratio of the intersection on the map bbox
        const intersection = intersect(suggBbox, mapBBox)

        // get the area of both
        intersectArea = intersection ? area(intersection) : 0
    }
    const mapArea = area(mapBBox)

    // finally figure out if we need to load new suggestions
    // if the intersection of the intersect is less than **70%** of the map area, we load new suggestions
    if (intersectArea / mapArea < 0.7) {
        console.log('loading suggestions')
        // first set the trigger to true
        isRequestingSuggestions.value = true

        // load the sugegstions from Overpass API
        loadReferenceAreaSuggestions().then(() => {
            // loading is finished, so we set the trigger back in one second
            setTimeout(() => isRequestingSuggestions.value = false, 1000)
        })
    } else {
        console.log(`no need to load suggestions, current coverage: ${(intersectArea / mapArea * 100).toFixed(1)}%`)
    }
})

// add some functions
export const createReferenceAreaFromSuggestion = (osm_id: string) => {
    // filter the current suggestions for the given osm_id
    const suggestion = areaSuggestions.peek().features.find(f => f.properties?.id === osm_id)

    // handle the creation
    if (!suggestion) {
        console.log(`createReferenceAreaFromSuggestion('${osm_id}'): could not find suggestion with id ${osm_id}`)
        return
    } else {
        const newCollection = {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: cloneDeep({...suggestion.geometry}),
                    properties: {
                        id: suggestion.properties!.id,
                        origin: 'osm',
                        area: area(suggestion),
                        landuse: suggestion.properties?.landuse
                    }
                }
            ]
        } as ReferenceArea
        
        // console.log(newFeature)
        referenceArea.value = newCollection
    }

    // finally, we flyIn far enough to allow for adding trees
    const areaCenter = center(suggestion)
    flyTo({
        zoom: 16.0, 
        center: {lat: areaCenter.geometry.coordinates[1], lng: areaCenter.geometry.coordinates[0]},
        pitch: 45
    })
}

// move the reference area into view
export const fitReferenceArea = () => {
    if (referenceArea.peek().features.length === 0) return
    
    // get the bounds with a 50m buffer and fit
    const bounds = bbox(buffer(referenceArea.peek(), 50, {units: 'meters'}))
    fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]])
}

// reference area tracking
const isDirty = signal(false)

effect(() => {
    if (referenceArea.value.features.length > 0 && !isDirty.peek()) {
        isDirty.value = true
    }
})

effect(() => {
    if (isDirty.value) posthog.capture('ReferenceAreaCreated')
})


/* --------------------------------------------------- 
 *   Reference Area Editing
 * --------------------------------------------------- 
 */

   // add a trigger to make the reference area editable
export const isReferenceAreaEditable = signal(false)

export const referenceAreaVertices = signal<{lon: number, lat: number, idx: number}[]>([])

effect(() => {
    if (referenceArea.value.features.length > 0) {
        referenceAreaVertices.value = referenceArea.value.features[0].geometry.coordinates[0].map((c, idx) => ({lon: c[0], lat: c[1], idx}))
    }
})

// add some function to move the reference area
export const onReferenceAreaVertexMove = (idx: number, newCoords: [number, number]) => {
    // copy over the reference area
    const newRefArea = cloneDeep(referenceArea.value.features[0])

    // update the coordinates
    newRefArea.geometry.coordinates[0][idx] = newCoords

    // update the reference area
    referenceArea.value = {
        type: 'FeatureCollection',
        features: [newRefArea]
    }
}

export const onRemoveReferenceAreaVertex = (idx: number) => {
    // copy over the reference area
    const newRefArea = cloneDeep(referenceArea.value.features[0])

    // remove the vertex
    newRefArea.geometry.coordinates[0].splice(idx, 1)

    // update the reference area
    referenceArea.value = {
        type: 'FeatureCollection',
        features: [newRefArea]
    }
}

// this is a bit more tricky, as we need to add the vertex at the right position
// in theory, this should work by taking the concave hull of the reference area plus
// the new point, as the concave hull is implemented using tin triangulation
export const onAddReferenceAreaVertex = (newCoords: [number, number]) => {
    // get the current reference Area
    const refArea = referenceArea.value.features[0]!

    // the event triggering this function could trigger without a reference area
    if (!refArea) return

    // get the distances to all existing vertices
    const distances = referenceAreaVertices.peek().map(v => distance(newCoords, [v.lon, v.lat], {units: 'meters'}))

    // find the closest vertex
    const closestVertex = distances.reduce((acc, cur, idx) => cur < distances[acc] ? idx : acc, 0)

    // add the new Coordinates at the given index to the reference area
    referenceArea.value = {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [refArea.geometry.coordinates[0].slice(0, closestVertex + 1).concat([newCoords], refArea.geometry.coordinates[0].slice(closestVertex + 1))]
            },
            properties: refArea.properties
        }]
    }
}
